<script setup lang="ts">
import type { PdpEcommerceBlockGalleryThumbnailsRedesignProps } from './PdpEcommerceBlockGalleryThumbnailsRedesign.props'
import emblaCarouselVue from 'embla-carousel-vue'

defineProps<PdpEcommerceBlockGalleryThumbnailsRedesignProps>()

const { isIos } = useDevice()
const ecommSliderEventBus = useEventBus<number>(
  'ecommerce-block-slider-event-bus'
)
const [emblaRef, emblaApi] = emblaCarouselVue({
  align: 'center',
  containScroll: 'keepSnaps',
  axis: 'y',
})

const activeSlide = ref(0)
const counter = ref(0)

const scrollIntoViewWithOffset = (el: HTMLElement, offset: number) => {
  window.scrollTo({
    behavior: 'smooth',
    top:
      el.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      offset,
  })
}

const headerHeight = useCssVar('--header-default-height', null, {
  observe: true,
})

// Go to `#ecommerce-block-slide-${index}`
const scrollIntoView = (index: number) => {
  const el = document.getElementById(`ecommerce-block-slide-${index}`)
  if (!el) return

  const galleryScroll = el.closest(
    '[data-pdp-gallery-scroll]'
  ) as HTMLElement | null

  // Desktop: scroll inside the sticky left column, not the page
  if (galleryScroll && window.matchMedia('(min-width: 1024px)').matches) {
    galleryScroll.scrollTo({
      behavior: 'smooth',
      top: el.offsetTop,
    })
    return
  }

  scrollIntoViewWithOffset(el, removePx(headerHeight.value))
}

ecommSliderEventBus.on(intersectedSlide => {
  activeSlide.value = intersectedSlide

  // this check fixes the bug of thumbnail slider that is not starting from the first slide/index
  if (counter.value === 0 && activeSlide.value !== 0 && window.scrollY < 100) {
    activeSlide.value = 0
    counter.value++
  }
  emblaApi.value?.scrollTo(activeSlide.value)
})
</script>

<template>
  <div
    class="top-3xl bottom-0 max-lg:hidden lg:pointer-events-none lg:absolute lg:left-0 lg:z-10 lg:w-[60px]"
  >
    <div
      class="top-0 flex h-full items-center lg:pointer-events-auto lg:sticky lg:max-h-[calc(100dvh-var(--header-default-height)-3.5rem*2)] lg:w-full"
    >
      <div ref="emblaRef" class="embla">
        <div class="embla__container flex gap-[2px] lg:flex-col">
          <div
            v-for="(media, index) in gallery"
            :key="`desktop-gallery-ecomm-block-${index}`"
            class="embla__slide"
          >
            <button
              :aria-label="
                $ts('accessibility.scrollToMedia', {
                  n: index,
                  total: gallery.length,
                })
              "
              aria-hidden="true"
              tabindex="-1"
              class="h-full w-full"
              :class="{ 'cursor-auto': index === activeSlide }"
              @click="scrollIntoView(index)"
            >
              <UIThumbnail
                is-pdp-redesign
                :selected="index === activeSlide"
                :is-video="media.type === 'TResponsiveVideo'"
              >
                <NuxtPicture
                  v-if="media.type === 'TResponsiveImage'"
                  :src="media.data"
                  provider="cloudinary"
                  sizes="60px"
                  quality="auto:low"
                  :modifiers="{ aspectRatio: '4:5' }"
                  :img-attrs="{
                    fetchpriority: 'high',
                  }"
                  fit="fill"
                  class="object-contain"
                  alt=""
                />
                <NuxtPicture
                  v-else-if="media.type === 'TResponsiveImage3D'"
                  src="/images/sample-3d-logo.webp"
                  class="object-contain"
                  sizes="60px"
                  :modifiers="{ aspectRatio: '4:5' }"
                  fit="fill"
                />
                <video
                  v-if="media.type === 'TResponsiveVideo'"
                  :id="`small_videoControl_${index}`"
                  :muted="true"
                  :loop="true"
                  controlsList="nofullscreen nodownload"
                  webkit-playsinline
                  playsinline
                  autoplay
                  :poster="posterVideo(media.data.cover)"
                  :data-image-index="index"
                  class="h-full w-full object-contain"
                >
                  <source
                    v-if="!isIos"
                    :src="
                      optimizeVideo(media.data.url, 'best', undefined, 'webm')
                    "
                    type="video/webm"
                  />
                  <source
                    :src="optimizeVideo(media.data.url, 'best')"
                    type="video/mp4"
                  />
                </video>
              </UIThumbnail>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.embla__slide {
  flex: 0 0 60px;
  height: 75px;

  @screen lg {
    flex-basis: 60px;
    width: var(--padding-x);
    aspect-ratio: 4 / 5;
  }
}
</style>
